import React, { useState, useRef } from 'react';
import { Helmet } from "react-helmet";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReCAPTCHA from "react-google-recaptcha";
import Banner from '../../components/Bannersection/banner';
import Faq from '../../components/Faq/faq';
import Cardcarousel from '../../components/Carousel/Cardcarousel';
import Modal from 'react-modal';
import { FaTimes } from 'react-icons/fa';
import './career.css';
import { FaLaptopCode, FaHandsHelping, FaRocket, FaMapMarkerAlt, FaBriefcase, FaMoneyBillWave, FaCloudUploadAlt, FaBullseye, FaEye, FaHandshake } from 'react-icons/fa';
import { BsClockHistory } from 'react-icons/bs';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function Career() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecaptchaSolved, setIsRecaptchaSolved] = useState(false);
  const [fileName, setFileName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const jobsPerPage = 3;
  const formRef = useRef(null);
  const recaptchaMainRef = useRef();
  const recaptchaModalRef = useRef();

  const pagename = [{ pagetitle: "Careers", subtitle: "Careers" }];

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileName(e.target.files[0].name);
    } else {
      setFileName("");
    }
  };

  const sendApplication = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    const isModal = event.target.id === 'modalCareerForm';
    const recaptchaRef = isModal ? recaptchaModalRef : recaptchaMainRef;
    const recaptchaValue = RECAPTCHA_SITE_KEY ? recaptchaRef.current?.getValue() : null;

    if (RECAPTCHA_SITE_KEY && !recaptchaValue) {
      toast.error('Please complete the reCAPTCHA.', { position: toast.POSITION.BOTTOM_CENTER });
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData(event.target);
    if (recaptchaValue) {
      formData.append('g-recaptcha-response', recaptchaValue);
    }

    try {
      const response = await fetch('/contact.php', { method: 'POST', body: formData });
      const result = await response.json();

      if (result.success) {
        toast.success('Application submitted successfully! We will get back to you soon.', { position: toast.POSITION.BOTTOM_CENTER });
        event.target.reset();
        setFileName("");
        recaptchaRef.current?.reset();
      } else {
        toast.error(result.message || 'Error submitting application. Please try again later.', { position: toast.POSITION.BOTTOM_CENTER });
        recaptchaRef.current?.reset();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error submitting application. Please try again later.', { position: toast.POSITION.BOTTOM_CENTER });
    } finally {
      setIsSubmitting(false);
    }
  };

  const jobOpenings = [
    {
      title: "Senior Full Stack Developer",
      experience: "3-5 Years",
      type: "Full Time",
      location: "Sydney / Hybrid",
      id: "FULLSTACK-01",
      description: "We are looking for a highly skilled Senior Full Stack Developer to lead our web development projects. You will be responsible for designing and implementing scalable web applications using modern technologies.",
      responsibilities: [
        "Develop and maintain complex web applications using MERN stack.",
        "Architect and design database schemas (MongoDB, PostgreSQL).",
        "Lead and mentor junior developers in best practices.",
        "Optimise applications for maximum speed and scalability.",
        "Collaborate with cross-functional teams to define and ship new features."
      ],
      requirements: [
        "5+ years of experience in full-stack development.",
        "Expertise in React, Node.js, and Express.",
        "Strong understanding of RESTful APIs and microservices.",
        "Experience with cloud platforms like AWS or Azure.",
        "Excellent problem-solving and communication skills."
      ]
    },
    {
      title: "MERN Stack Developer",
      experience: "2-4 Years",
      type: "Full Time",
      location: "Sydney",
      id: "MERN-02",
      description: "Join our dynamic team as a MERN Stack Developer. You will work on building robust and interactive web interfaces and back-end services for various client projects.",
      responsibilities: [
        "Build reusable components and front-end libraries for future use.",
        "Develop back-end services and APIs using Node.js and Express.",
        "Ensure the technical feasibility of UI/UX designs.",
        "Integrate front-end elements with server-side logic.",
        "Identify and fix bugs and performance issues."
      ],
      requirements: [
        "2-4 years of experience with MERN stack.",
        "Proficiency in JavaScript, React, and Node.js.",
        "Experience with MongoDB and Mongoose.",
        "Familiarity with Git and version control systems.",
        "Ability to work in an agile development environment."
      ]
    },
    {
      title: "Digital Marketing Specialist",
      experience: "1-3 Years",
      type: "Full Time",
      location: "Sydney",
      id: "DIGITAL-03",
      description: "We are seeking a creative Digital Marketing Specialist to manage our online presence and drive lead generation. You will be responsible for SEO, SEM, and social media campaigns.",
      responsibilities: [
        "Develop and execute SEO and SEM strategies to increase traffic.",
        "Manage social media accounts and create engaging content.",
        "Analyse website traffic and campaign performance metrics.",
        "Plan and manage email marketing campaigns.",
        "Stay up-to-date with digital marketing trends and tools."
      ],
      requirements: [
        "1-3 years of experience in digital marketing.",
        "Experience with Google Analytics, Google Ads, and Meta Ads.",
        "Strong understanding of SEO principles and tools (Ahrefs, SEMrush).",
        "Excellent writing and content creation skills.",
        "Data-driven mindset with analytical capabilities."
      ]
    },
    {
      title: "UI/UX Designer",
      experience: "2-4 Years",
      type: "Full Time",
      location: "Sydney / Remote",
      id: "DESIGN-01",
      description: "Are you passionate about creating beautiful and intuitive user experiences? Join us as a UI/UX Designer to craft world-class digital products for our clients.",
      responsibilities: [
        "Create wireframes, prototypes, and high-fidelity designs.",
        "Conduct user research and usability testing.",
        "Design consistent and scalable design systems.",
        "Collaborate with developers to ensure design integrity.",
        "Present design concepts and solutions to stakeholders."
      ],
      requirements: [
        "2-4 years of experience as a UI/UX Designer.",
        "Proficiency in design tools like Figma, Adobe XD, or Sketch.",
        "Strong portfolio demonstrating user-centered design approach.",
        "Understanding of HTML/CSS is a plus.",
        "Excellent eye for typography, colour, and layout."
      ]
    },
    {
      title: "QA Engineer",
      experience: "1-3 Years",
      type: "Full Time",
      location: "Sydney",
      id: "QA-01",
      description: "We are looking for a detail-oriented QA Engineer to ensure the quality and reliability of our software products. You will execute manual and automated tests.",
      responsibilities: [
        "Write and execute comprehensive test plans and test cases.",
        "Identify, document, and track software defects.",
        "Perform regression, integration, and performance testing.",
        "Collaborate with developers to resolve issues quickly.",
        "Contribute to the improvement of QA processes and tools."
      ],
      requirements: [
        "1-3 years of experience in software quality assurance.",
        "Experience with manual and automated testing tools (Selenium, Jest).",
        "Knowledge of software development lifecycle (SDLC).",
        "Strong attention to detail and analytical skills.",
        "Ability to work independently and as part of a team."
      ]
    }
  ];

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = jobOpenings.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(jobOpenings.length / jobsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    document.querySelector('.open-positions')?.scrollIntoView({ behavior: 'smooth' });
  };

  const openJobModal = (job) => { setSelectedJob(job); setModalIsOpen(true); };
  const closeJobModal = () => { setSelectedJob(null); setModalIsOpen(false); };

  const officeItems = [
    { title: 'Workspace-2', imageUrl: '/assets/img/office_setup/career/workspace2.jpeg' },
    { title: 'Front Office Structure', imageUrl: '/assets/img/office_setup/front_office_structure.jpg' },
    { title: 'Office Room', imageUrl: '/assets/img/office_setup/career/Office.jpeg' },
    { title: 'Recreation Area', imageUrl: '/assets/img/office_setup/recreation_area.jpg' },
  ];

  const faqitem = [
    { Faq_id: "collapseoneCareer", data_bs_target: "#collapseoneCareer", question: "How would you describe the team environment at DGi WORX?", answer: "Our workplace encourages collaboration, creativity, and open communication. We value fresh ideas and support every team member in doing meaningful work that drives real results." },
    { Faq_id: "collapsetwoCareer", data_bs_target: "#collapsetwoCareer", question: "Is there flexibility in terms of work location or schedule?", answer: "Yes, we provide flexible work arrangements depending on the role, including remote and hybrid options, to help maintain a healthy workâ€“life balance." },
    { Faq_id: "collapseThreeCareer", data_bs_target: "#collapseThreeCareer", question: "How does the company support employee learning and career growth?", answer: "We actively invest in skill development through training programs, mentorship, and hands-on project experience to help employees grow both personally and professionally." },
    { Faq_id: "collapsefourCareer", data_bs_target: "#collapsefourCareer", question: "What can candidates expect during the hiring process?", answer: "Our interview process is transparent and structured to understand your skills, mindset, and potential. It usually includes a discussion, skill evaluation, and a final interaction with the team." },
    { Faq_id: "collapsefiveCareer", data_bs_target: "#collapsefiveCareer", question: "Will I get a chance to work on different types of projects?", answer: "Absolutely. Team members often work on a variety of projects across domains, giving exposure to new challenges, technologies, and learning opportunities." }
  ];

  const mainFormFields = [
    { name: 'full_name', label: 'Full Name *', type: 'text', placeholder: 'Jane Smith', required: true, col: 'col-md-6' },
    { name: 'email', label: 'Email Address *', type: 'email', placeholder: 'jane@example.com', required: true, col: 'col-md-6' },
    { name: 'phone', label: 'Phone Number *', type: 'tel', placeholder: '+61 0452573682', required: true, col: 'col-md-6', maxLength: 15 },
    { name: 'position', label: 'Position Applying For *', type: 'select', required: true, col: 'col-md-6', options: [...jobOpenings.map(job => job.title), "General"] },
    { name: 'experience', label: 'Total Experience (Years)*', type: 'number', placeholder: 'e.g. 2.5', required: true, col: 'col-md-6', min: '0', step: '0.5' },
    { name: 'expected_salary', label: 'Expected Salary (AUD)', type: 'text', placeholder: 'e.g. $75,000', required: true, col: 'col-md-6' },
    { name: 'resume', label: 'Upload Resume (PDF/DOC) *', type: 'file', required: true, col: 'col-md-12', accept: '.pdf,.doc,.docx' },
    { name: 'message', label: 'Cover Letter / Message', type: 'textarea', placeholder: "Tell us why you'd be a great fit...", col: 'col-md-12' },
  ];

  const modalFormFields = [
    { name: 'full_name', label: 'Full Name *', type: 'text', placeholder: 'Jane Smith', required: true, col: 'col-md-12' },
    { name: 'email', label: 'Email Address *', type: 'email', placeholder: 'jane@example.com', required: true, col: 'col-md-12' },
    { name: 'phone', label: 'Phone Number *', type: 'tel', placeholder: '+61 0452573682', required: true, col: 'col-md-12', maxLength: 15 },
    { name: 'experience', label: 'Experience (Years)', type: 'number', placeholder: 'e.g. 2.5', required: true, col: 'col-md-6', min: '0', step: '0.5' },
    { name: 'expected_salary', label: 'Expected Salary (AUD)', type: 'text', placeholder: 'e.g. $75,000', required: true, col: 'col-md-6' },
    { name: 'resume', label: 'Upload Resume *', type: 'file', required: true, col: 'col-md-12', accept: '.pdf,.doc,.docx', isMini: true },
  ];

  const renderField = (field) => {
    const commonProps = { name: field.name, className: "form-control", placeholder: field.placeholder, required: field.required };

    if (field.type === 'select') {
      return (
        <select {...commonProps} className="form-select" defaultValue="">
          <option value="" disabled>Select a position</option>
          {field.options.map((option, idx) => <option key={idx} value={option}>{option}</option>)}
        </select>
      );
    }
    if (field.type === 'file') {
      return (
        <div className={`file-upload-wrapper ${field.isMini ? 'mini' : ''}`}>
          <input type="file" {...commonProps} className="file-upload-input" accept={field.accept} onChange={handleFileChange} />
          <div className="file-upload-icon"><FaCloudUploadAlt /></div>
          <p className="file-upload-text">{field.isMini ? (fileName || "Click to browse") : "Click to browse or drag & drop"}</p>
          {!field.isMini && fileName && <div className="file-name-display">{fileName}</div>}
        </div>
      );
    }
    if (field.type === 'textarea') return <textarea {...commonProps}></textarea>;
    if (field.type === 'tel') {
      return <input {...commonProps} type="tel" maxLength={field.maxLength} />;
    }
    if (field.type === 'number') {
      return <input {...commonProps} type="number" min={field.min} step={field.step} onInput={(e) => { if (e.target.value < 0) e.target.value = Math.abs(e.target.value); }} />;
    }
    return <input {...commonProps} type={field.type} />;
  };

  return (
    <div>
      <Helmet>
        <title>Careers | Join Our Team in Australia | DGi Worx</title>
        <meta name="description" content="Explore career opportunities at DGi WORX Australia. We are always looking for talented developers, designers, and marketers to join our innovative team." />
        <meta name="keywords" content="careers, jobs, hiring, full stack developer, digital marketing, dgi worx jobs, IT jobs Australia, Sydney tech jobs" />
        <link rel="canonical" href="https://dgiworx.com.au/careers" />
      </Helmet>

      <main>
        <h1 className='d-none'>Careers at DGi WORX Australia</h1>
        <Banner items={pagename} />

        <section className="about-area tp-about-two py-100 motto-section">
          <div className="container">
            <div className="row">
              <div className="col-lg-6">
                <div className="tp-about-two-wrap mb-10 wow fadeInRight" data-wow-duration="1s" data-wow-delay=".4s">
                  <div className="tp-about-two-thumb">
                    <div className="tp-about-circle">
                      <div className="tp-about-circle-two">
                        <img src="/assets/img/social_media/01.jpg" alt="Company culture and team" loading="lazy" />
                      </div>
                    </div>
                  </div>
                  <div className="tp-about-two-thumb-shape tp-thumb-common fix">
                    <div className="tp-thumb-common-overlay wow"></div>
                    <img src="/assets/img/social_media/02.jpg" alt="Modern office setup" loading="lazy" />
                  </div>
                  <div className="tp-about-two-thumb-shape-two d-none d-md-block">
                    <img src="/assets/img/about/two/dote.png" alt="" loading="lazy" />
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="tp-about-two-wrapper mb-10 wow fadeInLeft" data-wow-duration="1s" data-wow-delay=".4s">
                  <div className="tp-section mb-15">
                    <span className="tp-section-sub-title">Experience the Power of Digital Excellence</span>
                    <h2 className="tp-section-title">OUR MOTTO IS DIGITAL EXCELLENCE</h2>
                    <div className="tp-section-title-wrapper">
                      <p>At DGi WORX Australia, we believe that great ideas come from passionate people. We are always looking for talented individuals who are eager to learn, grow, and make a meaningful impact in the digital world. Join our team and work in an environment that encourages innovation, collaboration, and continuous learning while building a successful career in the digital industry.</p>
                    </div>
                  </div>
                  <div className="tp-about-two-list">
                    <ul>
                      <li><span><img src="/assets/img/icon/check-list.png" alt="Feature icon" loading="lazy" /></span>Innovative Work Environment</li>
                      <li><span><img src="/assets/img/icon/check-list.png" alt="Feature icon" loading="lazy" /></span>Career Growth Opportunities</li>
                      <li><span><img src="/assets/img/icon/check-list.png" alt="Feature icon" loading="lazy" /></span>Collaborative Team Culture</li>
                      <li><span><img src="/assets/img/icon/check-list.png" alt="Feature icon" loading="lazy" /></span>Real-World Project Experience</li>
                      <li><span><img src="/assets/img/icon/check-list.png" alt="Feature icon" loading="lazy" /></span>Learning and Development</li>
                      <li><span><img src="/assets/img/icon/check-list.png" alt="Feature icon" loading="lazy" /></span>Supportive Workplace</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="tp-about-two-shape"><div className="tp-about-two-shape-4"></div></div>
        </section>

        {/* <section className="project-area tp-project-4 fix py-50">
          <div className="tp-project-4-bg" data-background="/assets/img/project/five/project-5-bg-1.jpg" style={{ backgroundImage: `url(/assets/img/project/five/project-5-bg-1.jpg)` }}></div>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xl-10 col-sm-12">
                <div className="tp-section text-center mb-50">
                  <span className="tp-section-sub-title" style={{ color: 'red', fontWeight: 'bold' }}>Office Setup</span>
                  <h2 className="tp-section-title">Step Inside Our Office</h2>
                  <p>Explore images of our workspace, where creativity and innovation come to life.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="office">
            <div className="container">
              <Cardcarousel items={officeItems} />
            </div>
          </div>
        </section> */}

        <section className="open-positions py-100 bg-light">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="tp-section text-center mb-60">
                  <span className="tp-section-sub-title">Current Openings</span>
                  <h2 className="tp-section-title">Join Our Growing Team in Australia</h2>
                  <p className="mt-3">Work with a dynamic team of professionals and build the future of digital excellence. We are always looking for passionate individuals to join us.</p>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">
                {currentJobs.length > 0 ? (
                  currentJobs.map((job, idx) => (
                    <div key={idx} className="job-card">
                      <div className="job-info">
                        <h4>{job.title}</h4>
                        <div className="job-meta">
                          <span><FaBriefcase /> {job.experience}</span>
                          <span><BsClockHistory /> {job.type}</span>
                          <span><FaMapMarkerAlt /> {job.location}</span>
                        </div>
                      </div>
                      <div className="job-action">
                        <button className="apply-btn" onClick={() => openJobModal(job)}>Apply Now</button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-5"><h3>No positions found matching your criteria.</h3></div>
                )}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <button className="page-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                <div className="page-numbers">
                  {[...Array(totalPages)].map((_, i) => (
                    <button key={i + 1} className={`page-number ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => paginate(i + 1)}>{i + 1}</button>
                  ))}
                </div>
                <button className="page-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
              </div>
            )}
          </div>
        </section>

        <section className="why-join-us pt-100 pb-70">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="tp-section text-center mb-60">
                  <span className="tp-section-sub-title">Why DGi WORX?</span>
                  <h2 className="tp-section-title">Build Your Future With Us</h2>
                  <p className="mt-3 text-muted">We believe in innovation, constant learning, and growing together. Join a team that values your ideas and gives you the opportunity to make a real difference.</p>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-4 col-md-6 mb-30">
                <div className="benefit-card">
                  <div className="benefit-icon"><FaBullseye /></div>
                  <h3 className="benefit-title">Learn & Grow</h3>
                  <p className="benefit-text">Upskill, take ownership, and grow your career with mentorship and real growth opportunities.</p>
                </div>
              </div>
              <div className="col-lg-4 col-md-6 mb-30">
                <div className="benefit-card">
                  <div className="benefit-icon"><FaEye /></div>
                  <h3 className="benefit-title">Modern Tech Stack</h3>
                  <p className="benefit-text">Get hands-on experience with trending technologies while building impactful digital products.</p>
                </div>
              </div>
              <div className="col-lg-4 col-md-6 mb-30">
                <div className="benefit-card">
                  <div className="benefit-icon"><FaHandshake /></div>
                  <h3 className="benefit-title">Strong Team Culture</h3>
                  <p className="benefit-text">Collaborate with passionate people in a friendly, supportive, and idea-driven environment.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="application-section" ref={formRef}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="tp-section text-center mb-30">
                  <span className="tp-section-sub-title">Apply Now</span>
                  <h2 className="tp-section-title">Join Our Team</h2>
                  <p className="mt-2 text-muted">Fill out the form below to apply for a position. Please provide accurate information.</p>
                </div>
              </div>
            </div>
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="application-form-wrapper">
                  <form id="careerForm" action="" name="careerform" method="post" onSubmit={sendApplication} encType="multipart/form-data">
                    <input type="hidden" name="form_type" value="career_application" />
                    <div className='row'>
                      {mainFormFields.map((field, idx) => (
                        <div key={idx} className={field.col}>
                          <div className="form-group">
                            <label className="form-label">{field.label}</label>
                            {renderField(field)}
                          </div>
                        </div>
                      ))}
                    </div>
                    {RECAPTCHA_SITE_KEY && (
                      <div className="tp-form-input mb-20 d-flex flex-column align-items-center">
                        <label className="form-label mb-10 text-danger text-center">Verification Required *</label>
                        <ReCAPTCHA ref={recaptchaMainRef} sitekey={RECAPTCHA_SITE_KEY} onChange={(val) => setIsRecaptchaSolved(!!val)} />
                      </div>
                    )}
                    <div className="text-center mt-4">
                      <button className="tp-btn" style={{ padding: '15px 40px', fontSize: '18px', minWidth: 'max-content', width: 'auto' }} disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                      </button>
                    </div>
                  </form>
                  <ToastContainer />
                </div>
              </div>
            </div>
          </div>
        </section>

        <Faq items={faqitem} />
      </main>

      <Modal isOpen={modalIsOpen} onRequestClose={closeJobModal} contentLabel="Job Details" className="job-detail-modal" overlayClassName="job-detail-overlay">
        {selectedJob && (
          <div className="job-detail-container">
            <button className="modal-close-btn" onClick={closeJobModal}><FaTimes /></button>
            <div className="row">
              <div className="col-lg-6 job-info-column">
                <div className="modal-job-header">
                  <span className="modal-job-id">{selectedJob.id}</span>
                  <h2>{selectedJob.title}</h2>
                  <div className="modal-job-meta">
                    <span><FaBriefcase /> {selectedJob.experience}</span>
                    <span><BsClockHistory /> {selectedJob.type}</span>
                    <span><FaMapMarkerAlt /> {selectedJob.location}</span>
                  </div>
                </div>
                <div className="modal-job-body">
                  <div className="detail-section">
                    <h3>About the Role</h3>
                    <p>{selectedJob.description}</p>
                  </div>
                  <div className="detail-section">
                    <h3>Key Responsibilities</h3>
                    <ul>{selectedJob.responsibilities.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                  </div>
                  <div className="detail-section">
                    <h3>Requirements</h3>
                    <ul>{selectedJob.requirements.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 modal-form-column">
                <div className="modal-form-header">
                  <h3>Apply for this Position</h3>
                  <p>Submit your application below and our team will get back to you shortly.</p>
                </div>
                <div className="application-form-wrapper modal-form">
                  <form id="modalCareerForm" action="" name="modalcareerform" method="post" onSubmit={sendApplication} encType="multipart/form-data">
                    <input type="hidden" name="form_type" value="career_application" />
                    <input type="hidden" name="position" value={selectedJob.title} />
                    <div className="row">
                      {modalFormFields.map((field, idx) => (
                        <div key={idx} className={field.col}>
                          <div className="form-group">
                            <label className="form-label">{field.label}</label>
                            {renderField(field)}
                          </div>
                        </div>
                      ))}
                    </div>
                    {RECAPTCHA_SITE_KEY && (
                      <div className="tp-form-input mb-20 d-flex flex-column align-items-center">
                        <label className="form-label mb-10 text-danger text-center">Verification Required *</label>
                        <ReCAPTCHA ref={recaptchaModalRef} sitekey={RECAPTCHA_SITE_KEY} onChange={(val) => setIsRecaptchaSolved(!!val)} />
                      </div>
                    )}
                    <div className="text-center mt-3">
                      <button className="tp-btn" style={{ width: '100%', minWidth: 'max-content' }} disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Career;
